import type {
  CanonicalFieldKey,
  PriceImportMappingColumn,
  PriceImportMappingSpec,
  PriceImportRun,
} from 'apis/price-imports';

/**
 * The editable model behind the mapping editor.
 *
 * One row per SOURCE column — the file's reality is the fixed axis, the
 * template field is the assignment. The draft is deliberately simpler than a
 * full MappingSpec: an operator assigns targets; the transform vocabulary
 * belongs to inference. What keeps the two honest together is the reuse rule
 * in buildSpecFromDraft — an untouched assignment keeps its original spec
 * column verbatim, transforms included, so correcting ONE column does not
 * quietly downgrade the others to identity.
 */

export const REQUIRED_TARGETS: CanonicalFieldKey[] = ['source_name', 'price', 'price_unit'];

/** Every assignable template field, in template order. */
export const ASSIGNABLE_TARGETS: CanonicalFieldKey[] = [
  'source_name',
  'price',
  'price_unit',
  'supplier_code',
  'universal_name',
  'catalog_category',
  'brand',
  'origin',
  'base_unit',
  'net_content',
  'item_unit',
  'pack_unit',
  'pack_size',
  'state',
  'attr1_type',
  'attr1_value',
  'attr2_type',
  'attr2_value',
  'attr3_type',
  'attr3_value',
  'attr4_type',
  'attr4_value',
  'notes',
  'is_active',
];

export interface DraftColumn {
  sourceIndex: number;
  header: string;
  samples: string[];
  /** null = ignored. */
  target: CanonicalFieldKey | null;
  /** The spec column this assignment came from, kept to preserve transforms. */
  original: PriceImportMappingColumn | null;
}

export interface MappingDraft {
  headerRowIndex: number;
  columns: DraftColumn[];
  /**
   * "Every price is per ___" — the file has no unit column and the sheet says
   * it once. Becomes a constant price_unit column in the spec.
   */
  priceUnitConstant: string;
  /**
   * Spec columns the editor cannot represent per-source: multi-source concats
   * and constants other than the price unit. Carried through unchanged unless
   * an operator assignment claims the same target.
   */
  extras: PriceImportMappingColumn[];
}

const identityColumn = (target: CanonicalFieldKey, sourceIndex: number): PriceImportMappingColumn => ({
  target,
  sourceColumnIndexes: [sourceIndex],
  constant: null,
  transform: {
    kind: 'identity',
    separator: null,
    index: null,
    scale: null,
    map: null,
    fallback: null,
    fillDown: false,
  },
});

const constantColumn = (target: CanonicalFieldKey, value: string): PriceImportMappingColumn => ({
  target,
  sourceColumnIndexes: [],
  constant: value,
  transform: {
    kind: 'constant',
    separator: null,
    index: null,
    scale: null,
    map: null,
    fallback: null,
    fillDown: false,
  },
});

export function buildDraftFromRun(run: PriceImportRun): MappingDraft | null {
  const sample = run.sampleGrid;
  if (!sample || sample.width === 0) return null;

  const spec = run.mappingSpec;
  const headerRowIndex = spec?.headerRowIndex ?? 0;
  const headerRow = sample.headerRows[headerRowIndex] ?? [];

  const bySource = new Map<number, PriceImportMappingColumn>();
  const extras: PriceImportMappingColumn[] = [];
  let priceUnitConstant = '';

  (spec?.columns ?? []).forEach((column) => {
    if (column.sourceColumnIndexes.length === 1 && column.constant == null) {
      bySource.set(column.sourceColumnIndexes[0], column);
      return;
    }

    // The one constant the editor understands natively.
    if (column.target === 'price_unit' && column.constant != null) {
      priceUnitConstant = column.constant;
      return;
    }

    extras.push(column);
  });

  const columns: DraftColumn[] = Array.from({ length: sample.width }, (_, sourceIndex) => {
    const original = bySource.get(sourceIndex) ?? null;

    return {
      sourceIndex,
      header: String(headerRow[sourceIndex] ?? '').trim() || columnLetter(sourceIndex),
      samples: sample.sampleRows
        .slice(0, 3)
        .map((row) => row[sourceIndex])
        .filter((cell): cell is string => Boolean(cell && String(cell).trim())),
      target: original?.target ?? null,
      original,
    };
  });

  return { headerRowIndex, columns, priceUnitConstant, extras };
}

export function columnLetter(index: number): string {
  let value = index;
  let label = '';

  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);

  return label;
}

export interface DraftValidation {
  ok: boolean;
  /** Keyed messages, so the editor can place them next to the culprit. */
  errors: string[];
}

export function validateDraft(draft: MappingDraft): DraftValidation {
  const errors: string[] = [];
  const assigned = draft.columns.filter((column) => column.target != null);

  const counts = new Map<CanonicalFieldKey, number>();
  assigned.forEach((column) =>
    counts.set(column.target as CanonicalFieldKey, (counts.get(column.target as CanonicalFieldKey) ?? 0) + 1)
  );

  counts.forEach((count, target) => {
    if (count > 1) errors.push(`${target} is assigned to ${count} columns — pick one.`);
  });

  REQUIRED_TARGETS.forEach((target) => {
    const isAssigned = counts.has(target);
    const isConstant = target === 'price_unit' && draft.priceUnitConstant.trim().length > 0;

    if (!isAssigned && !isConstant) {
      errors.push(
        target === 'price_unit'
          ? 'price_unit needs a column, or state the unit every price is per.'
          : `${target} is not assigned, and a row cannot import without it.`
      );
    }
  });

  if (draft.priceUnitConstant.trim() && counts.has('price_unit')) {
    errors.push('price_unit has both a column and a constant — keep one.');
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Draft -> the spec PUT /:id/mapping expects.
 *
 * The reuse rule: an assignment whose target matches its original spec column
 * keeps that column verbatim — inference's extract_number, lookups and
 * fillDowns survive an unrelated correction. A changed assignment becomes a
 * plain identity read of that source column.
 */
export function buildSpecFromDraft(draft: MappingDraft): PriceImportMappingSpec {
  const assignedTargets = new Set(
    draft.columns.filter((column) => column.target != null).map((column) => column.target as CanonicalFieldKey)
  );

  const columns: PriceImportMappingColumn[] = draft.columns
    .filter((column) => column.target != null)
    .map((column) =>
      column.original && column.original.target === column.target
        ? column.original
        : identityColumn(column.target as CanonicalFieldKey, column.sourceIndex)
    );

  if (draft.priceUnitConstant.trim() && !assignedTargets.has('price_unit')) {
    columns.push(constantColumn('price_unit', draft.priceUnitConstant.trim()));
    assignedTargets.add('price_unit');
  }

  // Operator assignments win over inference leftovers on the same target.
  draft.extras.forEach((extra) => {
    if (!assignedTargets.has(extra.target)) columns.push(extra);
  });

  const sourceNameIndex = draft.columns.find((column) => column.target === 'source_name')?.sourceIndex ?? null;

  return {
    headerRowIndex: draft.headerRowIndex,
    dataStartRowIndex: draft.headerRowIndex + 1,
    columns,
    rowFilters: [
      { kind: 'drop_if_all_blank', sourceColumnIndex: null, value: null },
      // A line with no product name is a spacer or a total, not a product.
      ...(sourceNameIndex != null
        ? [{ kind: 'drop_if_blank' as const, sourceColumnIndex: sourceNameIndex, value: null }]
        : []),
    ],
    sections: { kind: 'none', detectColumnIndex: null, target: 'none' },
    confidence: 1,
    notes: 'Mapped by hand in the CMS.',
  };
}
